import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Users, ExternalLink, LogOut, LogIn, ShieldCheck } from "lucide-react";

const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/assets/rrdc-logo.png`;
const STAFF_SNAPSHOT_URL = "https://rrdcstaffsnapshot.com";

export default function SupervisorLanding() {
  const { user, loginSupervisor, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  const isSupervisor = user && user.user_type === "supervisor";

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      toast.error("Username and password required");
      return;
    }
    setLoading(true);
    try {
      await loginSupervisor(form.username.trim().toLowerCase(), form.password);
      toast.success("Welcome");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid supervisor credentials");
    } finally {
      setLoading(false);
    }
  };

  // ───────── NOT LOGGED IN: show inline supervisor login ─────────
  if (!isSupervisor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-100 px-4 py-10" data-testid="supervisor-landing">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img src={LOGO_URL} alt="RRDC" className="w-24 h-24 mb-3 object-contain" />
            <h1 className="font-heading text-3xl font-bold text-blue-900 tracking-tight">Supervisor Portal</h1>
            <p className="text-sm text-slate-600 mt-1">Rolling River Day Camp</p>
          </div>

          <Card className="border-blue-200 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-blue-700" />
                <h2 className="font-bold text-blue-800">Sign In</h2>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="sv-username" className="text-xs font-semibold text-slate-700">Username</Label>
                  <Input
                    id="sv-username"
                    type="text"
                    autoComplete="username"
                    placeholder="mariners / skippers / navigators"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    className="mt-1"
                    data-testid="supervisor-landing-username"
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="sv-password" className="text-xs font-semibold text-slate-700">Password</Label>
                  <Input
                    id="sv-password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="mt-1"
                    data-testid="supervisor-landing-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-700 hover:bg-blue-800 text-white"
                  data-testid="supervisor-landing-submit"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-[11px] text-center text-slate-500 mt-6">
            Rolling River Day Camp • Est. 1992
          </p>
        </div>
      </div>
    );
  }

  // ───────── LOGGED IN as supervisor: show 2-card chooser ─────────
  const divisionLabel = user?.group_name || (user?.division || "").replace(/^\w/, (c) => c.toUpperCase());

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-slate-100 px-4 py-10" data-testid="supervisor-landing-chooser">
      <div className="max-w-4xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="RRDC" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="font-heading text-2xl font-bold text-blue-900 leading-tight">Supervisor Portal</h1>
              {divisionLabel && (
                <p className="text-xs text-slate-600">Logged in as <span className="font-semibold">{divisionLabel}</span></p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              logout();
              toast.success("Logged out");
            }}
            className="text-slate-600 hover:text-rose-700"
            data-testid="supervisor-landing-logout"
          >
            <LogOut className="w-4 h-4 mr-1" /> Log Out
          </Button>
        </div>

        {/* Cards */}
        <div className="grid gap-5 sm:grid-cols-2">
          {/* Camper Snapshot */}
          <Card
            className="border-blue-200 hover:border-blue-400 hover:shadow-xl transition-all cursor-pointer group"
            onClick={() => navigate("/supervisor/dashboard")}
            data-testid="card-camper-snapshot"
          >
            <div className="h-1.5 bg-blue-600 rounded-t-lg" />
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Users className="w-7 h-7 text-blue-700" />
                </div>
                <span className="text-[10px] uppercase tracking-wider text-blue-700 font-bold bg-blue-50 px-2 py-1 rounded-full">Active session</span>
              </div>
              <h3 className="font-heading font-bold text-xl text-blue-900 mb-1">Camper Snapshot</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Campers, groups, attendance, incident reports, buddy sheets, intramurals & messaging.
              </p>
              <div className="mt-4 flex items-center text-sm font-semibold text-blue-700 group-hover:translate-x-1 transition-transform">
                Open Dashboard →
              </div>
            </CardContent>
          </Card>

          {/* RRDC Staff Snapshot — external */}
          <Card
            className="border-amber-200 hover:border-amber-400 hover:shadow-xl transition-all cursor-pointer group"
            onClick={() => window.open(STAFF_SNAPSHOT_URL, "_blank", "noopener,noreferrer")}
            data-testid="card-staff-snapshot"
          >
            <div className="h-1.5 bg-amber-500 rounded-t-lg" />
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ShieldCheck className="w-7 h-7 text-amber-700" />
                </div>
                <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-50 px-2 py-1 rounded-full flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> External
                </span>
              </div>
              <h3 className="font-heading font-bold text-xl text-amber-900 mb-1">RRDC Staff Snapshot</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Staff directory, scheduling & operations at <span className="font-mono text-xs">rrdcstaffsnapshot.com</span>.
              </p>
              <div className="mt-4 flex items-center text-sm font-semibold text-amber-700 group-hover:translate-x-1 transition-transform">
                Open in New Tab <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </div>
            </CardContent>
          </Card>
        </div>

        <p className="text-[11px] text-center text-slate-500 mt-10">
          Rolling River Day Camp • Est. 1992
        </p>
      </div>
    </div>
  );
}
